
import React from 'react';
import { X, FileText, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App.jsx';

const ServiceDetailsModal = ({ isOpen, onClose, invoice, chatInfo }) => {
    const navigate = useNavigate();
    const { fullUserData } = useAppContext();

    if (!isOpen || !invoice) return null;

    const renderLineItem = (description, price, key) => (
        <li key={key} className="flex justify-between items-center py-1.5 border-b border-gray-100 last:border-0">
            <span className="text-gray-600 text-xs pr-2 truncate">{description}</span>
            <span className="font-semibold text-gray-800 text-xs whitespace-nowrap">R$ {(parseFloat(price) || 0).toFixed(2).replace('.', ',')}</span>
        </li>
    );

    const handlePrint = () => {
        const allItems = [
            ...(invoice.includedProposals || []).flatMap(q => q.items || []),
            ...(invoice.newItems || [])
        ];

        if (invoice.discount > 0) {
            allItems.push({
                description: 'Desconto Aplicado',
                price: -invoice.discount
            });
        }

        // LÓGICA SEGURA (Sem buscas extras que causem erro de permissão)
        // Busca o nome da empresa apenas nos dados já acessíveis ao usuário
        const serviProfile = chatInfo?.participantDetails?.[chatInfo.serviId] || {};

        // Tenta encontrar o companyName nas fontes disponíveis
        const finalCompanyName =
            invoice.serviDetails?.companyName ||
            serviProfile.companyName ||
            (invoice.includedProposals || []).find(p => p.serviDetails?.companyName)?.serviDetails?.companyName ||
            (fullUserData?.profile_servi?.companyName); // Funciona se o prestador estiver logado

        navigate('/print-quote', {
            state: {
                message: {
                    type: 'quote',
                    timestamp: { seconds: Date.now() / 1000 },
                    quote: {
                        ...invoice,
                        items: allItems,
                        serviDetails: {
                            name: serviProfile.name || 'Prestador',
                            companyName: finalCompanyName || null,
                            photoUrl: serviProfile.photoUrl || null
                        },
                        clientDetails: chatInfo?.participantDetails?.[chatInfo.clientId] || {}
                    }
                },
                chatInfo
            }
        });
        onClose();
    };

    const scrollbarStyles = `
        .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #e2e8f0;
            border-radius: 10px;
        }
    `;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40 backdrop-blur-[2px]">
            <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />
            <div className="bg-white w-full max-w-[320px] rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[75vh]">
                {/* Header Compacto */}
                <div className="p-3 border-b flex justify-between items-center bg-gray-50 shrink-0">
                    <div className="flex items-center text-blue-600">
                        <FileText className="w-4 h-4 mr-2" />
                        <h3 className="font-bold text-sm">Resumo do Serviço</h3>
                    </div>
                    <div className="flex items-center space-x-1">
                        <button
                            onClick={handlePrint}
                            title="Imprimir Recibo"
                            className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-blue-600"
                        >
                            <Printer className="w-4 h-4" />
                        </button>
                        <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-full transition-colors">
                            <X className="w-5 h-5 text-gray-400" />
                        </button>
                    </div>
                </div>

                <div className="p-4 overflow-y-auto custom-scrollbar">
                    <div className="mb-4 text-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Total Pago</p>
                        <p className="text-2xl font-black text-green-600">
                            R$ {invoice.total.toFixed(2).replace('.', ',')}
                        </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <h4 className="font-bold text-gray-700 text-[10px] uppercase mb-2 border-b pb-1">Itens e Serviços</h4>
                        <ul className="space-y-0.5">
                            {(invoice.includedProposals || []).flatMap((q, i) =>
                                (q.items || []).map((item, j) => renderLineItem(item.description, item.price, `quote-${i}-${j}`))
                            )}
                            {(invoice.newItems || []).map((item, i) => renderLineItem(item.description, item.price, `new-${i}`))}

                            {invoice.discount > 0 && (
                                <li className="flex justify-between items-center py-1.5 text-red-600 border-t border-dashed mt-1">
                                    <span className="font-bold text-[10px] italic">Desconto</span>
                                    <span className="font-bold text-xs">- R$ {invoice.discount.toFixed(2).replace('.', ',')}</span>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                <div className="p-3 bg-gray-50 border-t shrink-0">
                    <button
                        onClick={onClose}
                        className="w-full py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition active:scale-[0.98]"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ServiceDetailsModal;
